import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { useDebounce } from '../hooks'
import { Pagination, Button } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './PlayersList.module.scss'

interface Player {
  tc_competitor_id: string
  ext_competitor_id: string
  tc_name: string
  ext_name: string
  tc_short_name: string
  ext_short_name: string
  tc_abbreviation: string
  ext_abbreviation: string
  tc_team_type: string
  ext_team_type: string
  tc_country_code: string
  ext_country_code: string
  country_name: string
  sport_name: string
  catalog_source_name: string
  created_at: string
  updated_at: string
}

interface PlayersResponse {
  players: Player[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: {
    name: string
  }
  timestamp: string
}

const PlayersList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 100

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { data, isLoading, error, mutate } = useSWR(
    ['players', { page: currentPage, limit: pageSize, name: debouncedSearchTerm }],
    () => api.get('/players', { page: currentPage, limit: pageSize, name: debouncedSearchTerm?.trim() || undefined }),
    { revalidateOnFocus: false }
  )

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading players</h2>
          <p>Failed to fetch players: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  const playersData: PlayersResponse = data?.body ? JSON.parse(data.body) : data
  const players = playersData?.players || []
  const pagination = playersData?.pagination

  const getPlayerName = (player: Player) => {
    return player.tc_name || player.ext_name || 'Unknown Player'
  }

  const getPlayerShortName = (player: Player) => {
    return player.tc_short_name || player.ext_short_name || ''
  }

  const getPlayerAbbreviation = (player: Player) => {
    return player.tc_abbreviation || player.ext_abbreviation || ''
  }

  const getExternalId = (player: Player) => {
    return player.ext_competitor_id || 'N/A'
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Link
          to="/players/new"
          className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
        >
          + Create Player
        </Link>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Players</h1>
      </div>

      {/* Search and Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search players by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading players...</h3>
          <p>Please wait while we fetch the player data.</p>
        </div>
      ) : players.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No players found</h3>
          <p>
            {debouncedSearchTerm
              ? `No players match "${debouncedSearchTerm}". Try a different search term.`
              : 'Unable to load player data.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Name</div>
            <div>External ID</div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {players.map((player) => (
              <div key={player.tc_competitor_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>
                    {getPlayerName(player)}
                  </p>
                  {getPlayerShortName(player) && (
                    <p className={listTableStyles.secondaryText}>{getPlayerShortName(player)}</p>
                  )}
                  {getPlayerAbbreviation(player) && (
                    <p className={listTableStyles.secondaryText}>({getPlayerAbbreviation(player)})</p>
                  )}
                </div>

                <div className={listTableStyles.cellId}>
                  {getExternalId(player)}
                </div>

<div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                  <Link
                    to={`/players/${player.tc_competitor_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={setCurrentPage}
              itemName="players"
            />
          )}
        </>
      )}
    </div>
  )
}

export default PlayersList
